import { useHits, useInstantSearch, useSortBy } from 'react-instantsearch';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { useI18nSortByItems } from '@sorare/core/src/hooks/search/useI18nSortByItems';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { PlayerResultsProps } from 'searchCommonPlayer/AdvancedPlayerSearch/types';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { BenchObjectRow } from '../BenchObjectRow';
import { BenchObjectRow_composeTeamBenchObjectInterface } from '../BenchObjectRow/__generated__/index.graphql';
import { BenchObjectSkeletons } from '../BenchObjectSkeletons';
import { BenchObjectTable } from '../BenchObjectTable';
import { SearchBenchObjectTableHeader } from '../SearchBenchObjectTableHeader';

type Hit = BenchObjectRow_composeTeamBenchObjectInterface & {
  __typename: 'ComposeTeamBenchCommonPlayer';
};

export const SearchBenchObjectTable = ({
  indexes = [],
  onStatsClick,
  onListingsClick,
}: PlayerResultsProps & {
  onStatsClick: (playerSlug: string) => void;
  onListingsClick: (playerSlug: string) => void;
}) => {
  const sortItems = useI18nSortByItems(indexes);
  const { updateSort } = useMarketplaceLifecycle();

  const { refine, currentRefinement } = useSortBy({
    items: sortItems,
  });
  const { status } = useInstantSearch();
  const { items: commonPlayers } = useHits<Hit>();
  const loading = status === 'loading' || status === 'stalled';

  const { statsManager } = useManagePlayerSearchView();
  const rarity = statsManager[PlayerSearchViewAttributes.RARITY];
  const inSeason =
    statsManager[PlayerSearchViewAttributes.SEASONALITY] ===
    So5LeaderboardSeasonality.IN_SEASON;

  const sorts = (currentRefinement?.split(',') ||
    []) as PrintablePlayerSortName[];

  const sortBy = (_sorts: PrintablePlayerSortName[]) => {
    updateSort(_sorts);
    refine(_sorts.join(','));
  };

  return (
    <BenchObjectTable
      header={<SearchBenchObjectTableHeader sorts={sorts} refine={sortBy} />}
    >
      {loading ? (
        <BenchObjectSkeletons rowsNumber={20} />
      ) : (
        commonPlayers.map(commonPlayer => {
          return (
            <BenchObjectRow
              key={commonPlayer.id}
              benchObject={commonPlayer}
              onStatsClick={() => onStatsClick(commonPlayer.anyPlayer.slug)}
              onListingsClick={() =>
                onListingsClick(commonPlayer.anyPlayer.slug)
              }
              rarity={rarity}
              inSeason={inSeason}
            />
          );
        })
      )}
    </BenchObjectTable>
  );
};
